/**
 * Application Routes
 * Defines all routes for the admin application
 */

import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import {
  AgentListPage,
  AgentConfigPage,
  LoginPage,
  CliAuthPage,
  DeviceAuthPage,
  MonitoringPage,
  SkillsPage,
  SystemSettingsPage,
  ModelCatalogPage,
  ToolManagementPage,
  BusinessTemplatesPage,
  AuditPage,
  EvaluationPage,
  PlaygroundPage,
  ImageWorkflowsPage,
  SchedulesPage,
  ReportsOverviewPage,
  ReportsUserCostPage,
  ReportsPricingPage,
  ReportsTrialRiskPage,
  ReportsBehaviorPage,
  ReportsRevenuePage,
  CostCenterPage,
  PromptTemplatesPage,
  UsersPage,
  AnnotationBatchesPage,
  AnnotationExportPage,
  AnnotationWorkbenchPage,
  AnnotationStatsPage,
  ClassificationAnalyticsPage,
  MessageFeedbackPage,
  FeatureFlagsPage,
  SubscriptionSettingsPage,
  ReferralSettingsPage,
  AffiliateManagementPage,
  AcquisitionChannelsPage,
  NotificationTemplatesPage,
  ContentPacksPage,
  TracesPage,
  WelcomeTitlesPage,
  PetTipsPage,
  UIAssetsPage,
  PerformancePage,
  UserGrowthPage,
  WishesPage,
  FinancePage,
  AssetsPage,
  ProviderFinancePage,
  AppStoreReleaseWorkbenchPage,
  DesignSystemPage,
  PromoCodesPage,
} from '@/pages';
import { getLegacyReportRedirectPath } from '@/features/reports/reportNavigation';
import { CompanyFinancePage } from '@/features/company-finance/CompanyFinancePage';

// Check if user is authenticated
function isAuthenticated(): boolean {
  const token = localStorage.getItem('admin_token');
  return !!token;
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  if (!isAuthenticated()) {
    // Save the attempted path for redirect after login
    sessionStorage.setItem('admin_redirect_after_login', location.pathname);
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function ReportsLegacyRedirect() {
  const { tab } = useParams<{ tab?: string }>();
  return <Navigate to={getLegacyReportRedirectPath(tab)} replace />;
}

export function AppRoutes() {
  return (
    <Routes>
      {/* Login page - public */}
      <Route path="/login" element={<LoginPage />} />

      {/* CLI authorization consent (doractl browser login) - public;
          the page handles its own login-required state so it can preserve
          the full ?redirect/challenge/state query when routing to /login. */}
      <Route path="/cli-auth" element={<CliAuthPage />} />

      {/* Device authorization consent (doractl device-flow login) - public;
          the page handles its own login-required state so it can preserve
          the full ?user_code query when routing to /login. Unlike /cli-auth
          it does not redirect — the CLI polls for the token in the
          background after approval. */}
      <Route path="/device" element={<DeviceAuthPage />} />

      {/* Agent list - protected home page */}
      <Route path="/" element={
        <ProtectedRoute>
          <AgentListPage />
        </ProtectedRoute>
      } />

      {/* Agent configuration editor - protected */}
      <Route path="/agents/:agentType" element={
        <ProtectedRoute>
          <AgentConfigPage />
        </ProtectedRoute>
      } />

      {/* System Settings - protected */}
      <Route path="/system-settings" element={
        <ProtectedRoute>
          <SystemSettingsPage />
        </ProtectedRoute>
      } />

      {/* Model Catalog - protected */}
      <Route path="/model-catalog" element={
        <ProtectedRoute>
          <ModelCatalogPage />
        </ProtectedRoute>
      } />

      {/* Tool Management - protected */}
      <Route path="/tool-management" element={
        <ProtectedRoute>
          <ToolManagementPage />
        </ProtectedRoute>
      } />

      {/* Business Templates - protected */}
      <Route path="/business-templates" element={
        <ProtectedRoute>
          <BusinessTemplatesPage />
        </ProtectedRoute>
      } />

      {/* Skills Management - protected */}
      <Route path="/skills" element={
        <ProtectedRoute>
          <SkillsPage />
        </ProtectedRoute>
      } />

      {/* Service Monitoring - protected */}
      <Route path="/monitoring" element={
        <ProtectedRoute>
          <MonitoringPage />
        </ProtectedRoute>
      } />

      {/* Push Test - redirect to notification-templates */}
      <Route path="/push-test" element={<Navigate to="/notification-templates" replace />} />

      {/* Notification Templates - protected */}
      <Route path="/notification-templates" element={
        <ProtectedRoute>
          <NotificationTemplatesPage />
        </ProtectedRoute>
      } />

      {/* Evaluation Center - consolidated tab page */}
      <Route path="/evaluation" element={
        <ProtectedRoute>
          <EvaluationPage />
        </ProtectedRoute>
      } />
      <Route path="/evaluation/:tab" element={
        <ProtectedRoute>
          <EvaluationPage />
        </ProtectedRoute>
      } />

      {/* Legacy redirects for old evaluation URLs */}
      <Route path="/eval-scenarios" element={<Navigate to="/evaluation/scenarios" replace />} />
      <Route path="/eval-metrics" element={<Navigate to="/evaluation/metrics" replace />} />
      <Route path="/test-suites" element={<Navigate to="/evaluation/suites" replace />} />

      {/* Playground - protected */}
      <Route path="/playground" element={
        <ProtectedRoute>
          <PlaygroundPage />
        </ProtectedRoute>
      } />

      {/* Image Workflows - protected */}
      <Route path="/image-workflows" element={
        <ProtectedRoute>
          <ImageWorkflowsPage />
        </ProtectedRoute>
      } />

      {/* Unified Schedules (Temporal-backed) - protected */}
      <Route path="/schedules" element={
        <ProtectedRoute>
          <SchedulesPage />
        </ProtectedRoute>
      } />

      {/* Legacy redirect: old workflow-schedules → unified schedules */}
      <Route path="/workflow-schedules" element={<Navigate to="/schedules" replace />} />

      {/* Content Packs - protected */}
      <Route path="/content-packs" element={
        <ProtectedRoute>
          <ContentPacksPage />
        </ProtectedRoute>
      } />

      {/* Reports - protected */}
      <Route path="/reports" element={
        <ProtectedRoute>
          <Navigate to="/reports/overview" replace />
        </ProtectedRoute>
      } />
      <Route path="/reports/overview" element={
        <ProtectedRoute>
          <ReportsOverviewPage />
        </ProtectedRoute>
      } />
      <Route path="/reports/user-cost" element={
        <ProtectedRoute>
          <ReportsUserCostPage />
        </ProtectedRoute>
      } />
      <Route path="/reports/pricing" element={
        <ProtectedRoute>
          <ReportsPricingPage />
        </ProtectedRoute>
      } />
      <Route path="/reports/trial-risk" element={
        <ProtectedRoute>
          <ReportsTrialRiskPage />
        </ProtectedRoute>
      } />
      <Route path="/reports/behavior" element={
        <ProtectedRoute>
          <ReportsBehaviorPage />
        </ProtectedRoute>
      } />
      <Route path="/reports/revenue" element={
        <ProtectedRoute>
          <ReportsRevenuePage />
        </ProtectedRoute>
      } />
      <Route path="/reports/:tab" element={
        <ProtectedRoute>
          <ReportsLegacyRedirect />
        </ProtectedRoute>
      } />

      {/* Cost Center (模型与成本) - protected */}
      <Route path="/cost-center" element={
        <ProtectedRoute>
          <CostCenterPage />
        </ProtectedRoute>
      } />

      {/* Prompt Templates - protected */}
      <Route path="/prompt-templates" element={
        <ProtectedRoute>
          <PromptTemplatesPage />
        </ProtectedRoute>
      } />

      {/* User Management - protected */}
      <Route path="/users" element={
        <ProtectedRoute>
          <UsersPage />
        </ProtectedRoute>
      } />

      {/* Classification Analytics - protected */}
      <Route path="/classification-analytics" element={
        <ProtectedRoute>
          <ClassificationAnalyticsPage />
        </ProtectedRoute>
      } />

      {/* Message Feedback - protected */}
      <Route path="/message-feedback" element={
        <ProtectedRoute>
          <MessageFeedbackPage />
        </ProtectedRoute>
      } />

      {/* Feature Flags - protected */}
      <Route path="/feature-flags" element={
        <ProtectedRoute>
          <FeatureFlagsPage />
        </ProtectedRoute>
      } />

      {/* Audit - protected */}
      <Route path="/audit" element={
        <ProtectedRoute>
          <AuditPage />
        </ProtectedRoute>
      } />

      {/* Subscription Settings - protected */}
      <Route path="/subscription-settings/:tab?" element={
        <ProtectedRoute>
          <SubscriptionSettingsPage />
        </ProtectedRoute>
      } />

      {/* Referral Settings - protected */}
      <Route path="/referral-settings" element={
        <ProtectedRoute>
          <ReferralSettingsPage />
        </ProtectedRoute>
      } />

      {/* Affiliate Management (星推官) - protected */}
      <Route path="/affiliate-management" element={
        <ProtectedRoute>
          <AffiliateManagementPage />
        </ProtectedRoute>
      } />

      {/* Acquisition Channels (获客渠道) - protected */}
      <Route path="/acquisition-channels" element={
        <ProtectedRoute>
          <AcquisitionChannelsPage />
        </ProtectedRoute>
      } />

      {/* Annotation System - protected */}
      <Route path="/annotation/batches" element={
        <ProtectedRoute>
          <AnnotationBatchesPage />
        </ProtectedRoute>
      } />
      <Route path="/annotation/export" element={
        <ProtectedRoute>
          <AnnotationExportPage />
        </ProtectedRoute>
      } />
      <Route path="/annotation/workbench" element={
        <ProtectedRoute>
          <AnnotationWorkbenchPage />
        </ProtectedRoute>
      } />
      <Route path="/annotation/stats" element={
        <ProtectedRoute>
          <AnnotationStatsPage />
        </ProtectedRoute>
      } />

      {/* Traces - protected */}
      <Route path="/traces" element={
        <ProtectedRoute>
          <TracesPage />
        </ProtectedRoute>
      } />

      {/* Welcome Titles - protected */}
      <Route path="/welcome-titles" element={
        <ProtectedRoute>
          <WelcomeTitlesPage />
        </ProtectedRoute>
      } />

      {/* Pet Tips - protected */}
      <Route path="/pet-tips" element={
        <ProtectedRoute>
          <PetTipsPage />
        </ProtectedRoute>
      } />

      {/* UI Assets - protected */}
      <Route path="/ui-assets" element={
        <ProtectedRoute>
          <UIAssetsPage />
        </ProtectedRoute>
      } />

      {/* Performance - protected */}
      <Route path="/performance" element={
        <ProtectedRoute>
          <PerformancePage />
        </ProtectedRoute>
      } />

      {/* User Growth - protected */}
      <Route path="/user-growth" element={
        <ProtectedRoute>
          <UserGrowthPage />
        </ProtectedRoute>
      } />

      {/* Wish Pool - protected */}
      <Route path="/wishes" element={
        <ProtectedRoute>
          <WishesPage />
        </ProtectedRoute>
      } />

      {/* App Store Release Workbench - protected */}
      <Route path="/app-store-releases" element={
        <ProtectedRoute>
          <AppStoreReleaseWorkbenchPage />
        </ProtectedRoute>
      } />

      {/* Finance Ledger - protected */}
      <Route path="/finance" element={
        <ProtectedRoute>
          <FinancePage />
        </ProtectedRoute>
      } />
      <Route path="/finance/:tab" element={
        <ProtectedRoute>
          <FinancePage />
        </ProtectedRoute>
      } />
      <Route path="/company-finance" element={
        <ProtectedRoute>
          <CompanyFinancePage />
        </ProtectedRoute>
      } />

      {/* Promo Codes (Marketing) - protected */}
      <Route path="/promo-codes" element={
        <ProtectedRoute>
          <PromoCodesPage />
        </ProtectedRoute>
      } />

      {/* Provider Finance - protected */}
      <Route path="/provider-finance" element={
        <ProtectedRoute>
          <ProviderFinancePage />
        </ProtectedRoute>
      } />

      {/* Asset Ledger - protected */}
      <Route path="/assets" element={
        <ProtectedRoute>
          <AssetsPage />
        </ProtectedRoute>
      } />
      <Route path="/assets/:tab" element={
        <ProtectedRoute>
          <AssetsPage />
        </ProtectedRoute>
      } />

      {/* Design System showcase - protected */}
      <Route path="/design-system" element={
        <ProtectedRoute>
          <DesignSystemPage />
        </ProtectedRoute>
      } />

      {/* Catch-all redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
